import { postAndSelectSubsidiary, savePrevPath } from 'actions'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import ParentAndChain from 'features/EditHotel/ParentAndChain'
import useForm from 'hooks/useForm'
import { PMSnames } from 'main/hotelBrandsAndChains'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, useParams } from 'react-router'
import hotelApi from 'services/api/hotel'

import { getOtherHotelDetails } from '../../actions/hotelDetails'
import Dialog from '../../components/ReusableComponents/Dialog/index'
import CustomHeader from '../../components/ReusableComponents/HeaderTitles/index'
import LoadingIndicator from '../../components/ReusableComponents/LoadingIndicator'
import { getCultureCodeFromCountryCode } from '../../utils/helpers'
import AutoInviteInfo from './AutoInviteInfo/index'
import BottomBar from './BottomBar/index'
import CheckinCheckout from './CheckinCheckout/index'
import styles from './EditHotel.module.scss'
import { ReactComponent as ErrorIcon } from './error.svg'
import InvoiceAndBilling from './InvoiceAndBilling/index'
import Legal from './Legal/index'

const EditHotel = () => {
  const dispatch = useDispatch()
  const { id, limited } = useParams()

  const [formData, handleFormChange, setFormData] = useForm({
    merchantId: '',
    parentChainCode: '',
    chainCode: '',
    urlKey: '',
    reservationHotLine: '',
    reviewTripAdvisorPage: '',
    cultureCode: '',
    billingAddress: '',
    invoiceAddress: '',
    utcOffSet: '',
    tzTimeZoneName: '',
    termsUrl: '',
    privacyUrl: '',
    streetAddress: '',
    city: '',
    pms: '',
    externalHotelId: '',
    stateCode: '',
    postalCode: '',
    countryCode: '',
    checkInTime: '',
    checkOutTime: '',
    setGoogleEntityId: '',
    primaryWebsiteUrl: ''
  })

  const [isLoading, setLoading] = useState(true)
  const [initialPMS, setInitialPMS] = useState()
  const [isPmsDialogVisible, setPmsDialogVisibility] = useState(false)

  const { [id]: hotelDetails } = useSelector(s => s.hotelDetails.otherSubsidiaries)

  useEffect(() => {
    if (hotelDetails) {
      const { textMarketingUrlKey, ...restOfDetails } = hotelDetails

      setFormData({
        ...restOfDetails,
        streetAddress:
          restOfDetails.streetAddress == null ? '' : String(restOfDetails.streetAddress),
        urlKey:
          !textMarketingUrlKey || textMarketingUrlKey.startsWith('missing')
            ? ''
            : textMarketingUrlKey.includes('/')
            ? textMarketingUrlKey.split('/')[1]
            : textMarketingUrlKey
      })
      setInitialPMS(restOfDetails.pms)
      setLoading(false)
    } else {
      dispatch(getOtherHotelDetails(id))
    }
  }, [dispatch, hotelDetails, id, setFormData])

  useEffect(() => {
    const cultureCode = getCultureCodeFromCountryCode(formData.countryCode)

    if (cultureCode) {
      setFormData({ cultureCode })
    }
  }, [formData.countryCode, setFormData])

  const [parentAndChainValidated, setParentAndChainValidated] = useState()
  const [autoInviteValidated, setAutoInviteValidated] = useState()
  const [invoiceAndBillingValidated, setInvoiceAndBillingValidated] = useState()
  const [legaledValidated, setLegalValidated] = useState()

  const [isSubmitted, setSubmitted] = useState()

  const [updatingHotel, setUpdatingHotel] = useState(false)
  const [updateHotelError, setUpdateHotelError] = useState(null)

  function handleSubmit() {
    if (initialPMS !== formData.pms) {
      setPmsDialogVisibility(true)
    } else {
      updateHotelDetails()
    }
  }

  function updateHotelDetails() {
    const { chainCode, urlKey } = formData

    const postData = {
      ...formData,
      textMarketingUrlKey: chainCode ? chainCode + '/' + urlKey : urlKey
    }

    setUpdatingHotel(true)
    hotelApi
      .updateAsAdmin(postData, id)
      .then(() => {
        dispatch(savePrevPath('/hotelDetails'))
      })
      .then(() => {
        dispatch(postAndSelectSubsidiary(id))
      })
      .then(() => {
        setUpdatingHotel(false)
        setUpdateHotelError(null)
        setSubmitted(true)
      })
      .catch(error => {
        setUpdatingHotel(false)
        setUpdateHotelError(error?.message ?? String(error))
      })
  }

  return (
    <>
      {isSubmitted && <Redirect to={`/hotel/${id}`} />}
      {isLoading || updatingHotel ? (
        <Dialog>
          <LoadingIndicator white />
        </Dialog>
      ) : (
        <div className={styles.wrapper}>
          {updateHotelError && (
            <div className={styles.formError}>
              <ErrorIcon />
              {updateHotelError}
            </div>
          )}
          {isPmsDialogVisible && (
            <ConfirmationDialog
              onConfirm={updateHotelDetails}
              onClose={() => setPmsDialogVisibility(false)}
              onCancel={() => setPmsDialogVisibility(false)}
              positiveMessage={`Yes, update to ${PMSnames[formData.pms]}`}
              negativeMessage="Cancel"
              title="Confirm PMS update"
              hasDangerousAction
            >
              <p>
                You are updating this hotel’s PMS to {PMSnames[formData.pms]}. Hotel will
                migrate from {PMSnames[initialPMS]} to {PMSnames[formData.pms]} and this
                process will take approximately 10 minutes.
              </p>
            </ConfirmationDialog>
          )}
          <CustomHeader title={`Edit ${formData.hotelName || 'Hotel'}`} width={830} />
          <ParentAndChain
            handleFormChange={handleFormChange}
            formData={formData}
            handleValidated={setParentAndChainValidated}
            setFormData={setFormData}
            valid={parentAndChainValidated}
            disabled={false}
            done={true}
          />
          <AutoInviteInfo
            handleFormChange={handleFormChange}
            setFormData={setFormData}
            formData={formData}
            hotelName={formData.hotelName}
            streetAddress={formData.streetAddress}
            googleEntityId={formData.googleEntityId}
            externalHotelId={formData.externalHotelId}
            pms={formData.pms}
            handleValidated={setAutoInviteValidated}
            valid={autoInviteValidated}
            limited={!!limited}
          />
          <InvoiceAndBilling
            formData={formData}
            setFormData={setFormData}
            handleValidated={setInvoiceAndBillingValidated}
            valid={invoiceAndBillingValidated}
            limited={!!limited}
          />
          <Legal
            formData={formData}
            setFormData={setFormData}
            valid={legaledValidated}
            handleValidated={setLegalValidated}
            parentChainCode={formData.parentChainCode}
          />
          <CheckinCheckout formData={formData} setFormData={setFormData} />
          <BottomBar
            buttonLabel="Save changes"
            nextDisabled={!(autoInviteValidated && invoiceAndBillingValidated)}
            handleNext={handleSubmit}
            cancelLink={`/hotel/${id}`}
          />
        </div>
      )}
    </>
  )
}

export default EditHotel
